import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { CornerUpLeft } from 'lucide-react';
import React from 'react';

import { TabOption } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import type {
  AugustusPOE,
  MainTabKey,
  POEContent,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { cn } from '@/utils/classname';
import { safeExecute } from '@/utils/function.util';

const EVIDENCE_ACRONYMS = new Set([
  'bola',
  'ssrf',
  'oob',
  'id',
  'ids',
  'url',
  'uri',
  'api',
  'cwe',
  'llm',
  'mcp',
]);

// "mcptool.bola.negative_control" -> "BOLA Negative Control"; strips the
// mcptool. namespace and title-cases the rest, uppercasing known acronyms.
const humanizeEvidenceKey = (key: string): string =>
  key
    .replace(/^mcptool\./, '')
    .split(/[.\-_\s]+/)
    .filter(Boolean)
    .map(w =>
      EVIDENCE_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');

// Renders an arbitrary evidence value: objects/arrays as pretty JSON, long or
// multi-line strings in a scrollable code block, short scalars inline.
const renderEvidenceValue = (value: unknown): React.ReactNode => {
  if (typeof value === 'object' && value !== null) {
    return (
      <pre className="bg-chariot-background-primary text-chariot-text-primary max-h-96 overflow-auto rounded p-3 font-mono text-xs whitespace-pre-wrap">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  const text = String(value);
  if (text.length > 80 || text.includes('\n')) {
    return (
      <pre className="bg-chariot-background-primary text-chariot-text-primary max-h-96 overflow-auto rounded p-3 font-mono text-sm whitespace-pre-wrap">
        {text}
      </pre>
    );
  }
  return <span className="font-mono text-sm break-all">{text}</span>;
};

// Process Augustus LLM vulnerability scanner data
export const processAugustusDataSource = (
  file: POEContent | string,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>
) => {
  const augustusData = safeExecute(() => {
    return typeof file === 'string' ? JSON.parse(file) : file;
  }) as AugustusPOE | null;

  if (!augustusData) return;

  const { service, endpoint, goal, scan_findings, attack_findings } =
    augustusData;

  // Overview section with target information
  sections['augustus_overview'] = (
    <div className="space-y-4">
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <table className="w-full">
          <tbody>
            <tr className="border-chariot-stroke border-b">
              <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/4 px-4 py-3 font-medium">
                Service
              </td>
              <td className="text-chariot-text-primary px-4 py-3">
                <span className="bg-chariot-status-low border-chariot-stroke-low inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium text-white">
                  {service || 'Unknown'}
                </span>
              </td>
            </tr>
            <tr className="border-chariot-stroke border-b">
              <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/4 px-4 py-3 font-medium">
                Endpoint
              </td>
              <td className="text-chariot-text-primary px-4 py-3 font-mono text-sm break-all">
                <CopyToClipboard alwaysShowCopyIcon textToCopy={endpoint || ''}>
                  {endpoint || 'Unknown'}
                </CopyToClipboard>
              </td>
            </tr>
            <tr className="border-chariot-stroke border-b last:border-b-0">
              <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/4 px-4 py-3 align-top font-medium">
                Attack Goal
              </td>
              <td className="text-chariot-text-primary px-4 py-3">
                <div className="bg-chariot-background-primary rounded p-2 font-mono text-sm">
                  {goal || 'N/A'}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  tempTabs['INFORMATION'].push({
    label: 'Overview',
    value: 'augustus_overview',
  });

  // Create individual finding tabs in DETECTION
  const allFindings = [
    ...(scan_findings || []).map(f => ({ ...f, phase: 'Scan' })),
    ...(attack_findings || []).map(f => ({ ...f, phase: 'Attack' })),
  ];

  allFindings.forEach((finding, index) => {
    const {
      probe,
      description,
      category,
      prompt,
      response,
      confidence,
      phase,
      tool,
      parameter,
      evidence,
    } = finding;
    // The probe's ground-truth sentence (attempt goal) states, in plain terms,
    // what the finding demonstrates — surfaced as a prominent Summary so the
    // proof reads as an exploitation, not a routine tool call. Pulled out of the
    // raw evidence table to avoid duplication.
    const goalText =
      typeof (evidence as Record<string, unknown>)?.goal === 'string'
        ? ((evidence as Record<string, unknown>).goal as string)
        : undefined;
    const evidenceEntries = Object.entries(evidence || {}).filter(
      ([k, v]) => k !== 'goal' && v !== null && v !== undefined && v !== ''
    );
    const tabId = `finding_${index}`;
    const tabLabel = probe || `Finding ${index + 1}`;

    sections[tabId] = (
      <div className="space-y-4">
        {/* Summary: the probe's plain-language conclusion, foregrounded so the
            proof reads as an exploitation (e.g. a cross-identity read) rather
            than a routine successful tool call. */}
        {goalText && (
          <div className="border-chariot-stroke-high bg-chariot-status-high/10 rounded-lg border border-l-4 px-4 py-3">
            <div className="text-chariot-text-secondary mb-1 text-xs font-semibold tracking-wide uppercase">
              Summary
            </div>
            <p className="text-chariot-text-primary text-sm">{goalText}</p>
          </div>
        )}

        {/* Finding header */}
        <div className="border-chariot-stroke overflow-hidden rounded-lg border">
          <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-3">
            <span className="text-chariot-text-primary font-medium">
              {probe}
            </span>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium text-white',
                  phase === 'Attack'
                    ? 'bg-chariot-status-critical border-chariot-stroke-critical'
                    : 'bg-chariot-status-low border-chariot-stroke-low'
                )}
              >
                {phase}
              </span>
              {confidence !== undefined && (
                <span className="text-chariot-text-secondary text-xs">
                  {Math.round(confidence * 100)}% confidence
                </span>
              )}
            </div>
          </div>

          <table className="w-full">
            <tbody>
              {description && (
                <tr className="border-chariot-stroke border-b">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                    Description
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3">
                    {description}
                  </td>
                </tr>
              )}
              {category && (
                <tr className="border-chariot-stroke border-b">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                    Category
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3">
                    <span className="bg-chariot-text-purple/10 text-chariot-text-purple inline-flex items-center rounded-md px-2 py-1 text-xs font-medium">
                      {category}
                    </span>
                  </td>
                </tr>
              )}
              {tool && (
                <tr className="border-chariot-stroke border-b">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                    Tool
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3 font-mono text-sm">
                    {tool}
                    {parameter ? ` ( ${parameter} )` : ''}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Evidence: the probe's own recorded proof metadata, rendered
            structurally so it substantiates the finding for any probe (BOLA
            controls + identities, injection canary, SSRF flags, ...). */}
        {evidenceEntries.length > 0 && (
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <div className="bg-chariot-background-secondary px-4 py-2">
              <h5 className="text-chariot-text-primary text-sm font-medium">
                Evidence
              </h5>
            </div>
            <table className="w-full">
              <tbody>
                {evidenceEntries.map(([key, value]) => (
                  <tr
                    key={key}
                    className="border-chariot-stroke border-b align-top last:border-b-0"
                  >
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/4 px-4 py-3 align-top font-medium">
                      {humanizeEvidenceKey(key)}
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      {renderEvidenceValue(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Prompt section */}
        {prompt && (
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-2">
              <h5 className="text-chariot-text-primary flex items-center gap-2 text-sm font-medium">
                <ChatBubbleOvalLeftEllipsisIcon className="size-4" />
                Prompt
              </h5>
              <CopyToClipboard
                textToCopy={prompt}
                iconClassName="size-4 text-chariot-text-secondary"
                alwaysShowCopyIcon
              >
                <span className="sr-only">Copy prompt</span>
              </CopyToClipboard>
            </div>
            <div className="bg-chariot-background-primary max-h-64 overflow-auto p-4">
              <pre className="text-chariot-text-primary font-mono text-sm whitespace-pre-wrap">
                {prompt}
              </pre>
            </div>
          </div>
        )}

        {/* Response section */}
        {response && (
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-2">
              <h5 className="text-chariot-text-primary flex items-center gap-2 text-sm font-medium">
                <CornerUpLeft className="size-4" />
                Response
              </h5>
              <CopyToClipboard
                textToCopy={response}
                iconClassName="size-4 text-chariot-text-secondary"
                alwaysShowCopyIcon
              >
                <span className="sr-only">Copy response</span>
              </CopyToClipboard>
            </div>
            <div className="bg-chariot-background-primary max-h-96 overflow-auto p-4">
              <pre className="text-chariot-text-primary font-mono text-sm whitespace-pre-wrap">
                {response}
              </pre>
            </div>
          </div>
        )}
      </div>
    );

    tempTabs['DETECTION'].push({
      label: tabLabel,
      value: tabId,
    });
  });
};
